import { Form, getDRFFormError, Submit, useFormState, useSubmitHandler } from '@ff-it/form';
import { Alert, Box, Button, Stack, Table, Tbody, Td, Th, Thead, Tr } from '@ff-it/ui';
import {
  Breadcrumb,
  DeleteForm,
  EntityScene,
  Heading,
  IndexScene,
  ItemLoader,
  ModelScene,
  Page,
  PermissionDenied,
  useEntity,
  useModel,
} from 'components';
import type { ControlledTableColumns } from 'components/ControlledTable';
import { useHasPerm } from 'core/permissions';
import type { Outlet } from 'modules/supplier/outletOrServiceCategory/types';
import type { PriceList } from 'modules/supplier/pricelist/types';
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams, useRoutes } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSWRConfig } from 'swr';
import invariant from 'tiny-invariant';
import { actionErrorOrThrow } from 'utilities';

import { hasBands } from '../common/util';
import { useRadioOutlets } from '../common/useRadioOutlets';
import type {
  RadioPackageDetail,
  RadioPackageOption,
  RadioPackageOptionSummary,
  RadioPackageSummary,
  RadioPriceListRates,
} from '../types';
import {
  catalogPricingKindLabel,
  createPackageOption,
  createRadioPackage,
  normalizePackageCreate,
  normalizePackageMetadata,
  normalizePackageOption,
  toPackageMetadataFormValues,
  toPackageOptionFormValues,
  type PackageCreateFormValues,
  type PackageMetadataFormValues,
  type PackageOptionFormValues,
} from './formValues';
import { PackageCreateFields, PackageMetadataFields, PackageOptionFields } from './PackageForm';

const packagePermissions = {
  view: 'supplier.view_vendor',
  add: 'supplier.change_vendor',
  change: 'supplier.change_vendor',
  delete: 'supplier.change_vendor',
};

const packageColumns: ControlledTableColumns<RadioPackageSummary> = [
  {
    content: 'Name',
    key: 'name',
    required: true,
    render: ({ id, name }) => <Link to={`${id}`}>{name}</Link>,
  },
  {
    content: 'Options',
    key: 'option_count',
    width: 120,
  },
];

function DeleteSubmissionError(): ReactElement | null {
  const { submitError } = useFormState({ subscription: { submitError: true } });
  if (!submitError) return null;
  const messages = Array.isArray(submitError) ? submitError : [submitError];
  return (
    <Box ml="2">
      {messages.map((message) => (
        <div key={String(message)}>{String(message)}</div>
      ))}
    </Box>
  );
}

function useInvalidatePackageIndex(packagesUrl: string): () => void {
  const { mutate } = useSWRConfig();
  return useCallback(() => {
    void mutate(
      (key: unknown) =>
        Boolean(key && typeof key === 'object' && 'url' in key && (key as { url?: string }).url === packagesUrl),
      undefined,
      { revalidate: true },
    );
  }, [mutate, packagesUrl]);
}

function RadioFormData({
  radioUrl,
  vendorId,
  children,
}: {
  radioUrl: string;
  vendorId: number;
  children: (radio: RadioPriceListRates, outlets: Outlet[]) => ReactNode;
}): ReactElement {
  const outlets = useRadioOutlets(vendorId);
  return (
    <ItemLoader<RadioPriceListRates> url={radioUrl}>
      {({ item: radio }) => (
        <>
          {!outlets.length && <Alert variant="warning">No radio outlets</Alert>}
          {!hasBands(radio.bands) && (
            <Alert variant="warning">Configure radio bands before maintaining packages.</Alert>
          )}
          {!radio.ratecards.some((card) => card.default) && (
            <Alert variant="warning">Configure an ordinary default radio rate card before saving packages.</Alert>
          )}
          {children(radio, outlets)}
        </>
      )}
    </ItemLoader>
  );
}

function PackageCreateScreen({
  packagesUrl,
  radioUrl,
  vendorId,
}: {
  packagesUrl: string;
  radioUrl: string;
  vendorId: number;
}): ReactElement {
  const navigate = useNavigate();
  const invalidateIndex = useInvalidatePackageIndex(packagesUrl);
  const canChange = useHasPerm(packagePermissions.change);
  const onSubmit = useSubmitHandler<PackageCreateFormValues, RadioPackageDetail>(
    { url: packagesUrl, method: 'POST' },
    {
      mapValues: normalizePackageCreate,
      onSuccess: (value) => {
        invalidateIndex();
        toast.success('Radio package has been created.');
        navigate(`../${value.id}`);
      },
    },
  );

  if (!canChange) return <PermissionDenied />;

  return (
    <RadioFormData radioUrl={radioUrl} vendorId={vendorId}>
      {(radio, outlets) => (
        <Page>
          <Breadcrumb to={null}>Create</Breadcrumb>
          <Heading title="Create radio package" />
          <Form noValidate autoComplete="off" onSubmit={onSubmit} initialValues={createRadioPackage()}>
            <PackageCreateFields bands={radio.bands} outlets={outlets} />
            <Submit>Create</Submit>
          </Form>
        </Page>
      )}
    </RadioFormData>
  );
}

function OptionSummaryTable({ options }: { options: RadioPackageOptionSummary[] }): ReactElement {
  return (
    <Box style={{ overflowX: 'auto' }} mb="3">
      <Table size="sm" textSize="sm" border="subtle">
        <Thead variant="default">
          <Tr>
            <Th>Name</Th>
            <Th>Pricing</Th>
            <Th>Distribution</Th>
          </Tr>
        </Thead>
        <Tbody>
          {options.map((option) => (
            <Tr key={option.id}>
              <Td>
                <Link to={`options/${option.id}`}>{option.name}</Link>
              </Td>
              <Td>{catalogPricingKindLabel(option.pricing_kind)}</Td>
              <Td>{option.distribution_mode || 'None'}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

function PackageDetailScreen({ packagesUrl }: { packagesUrl: string }): ReactElement {
  const { item, endpoint, setItem } = useEntity<RadioPackageDetail>();
  const canChange = useHasPerm(packagePermissions.change);
  const invalidateIndex = useInvalidatePackageIndex(packagesUrl);
  const initialValues = useMemo(() => toPackageMetadataFormValues(item), [item]);
  const onSubmit = useSubmitHandler<PackageMetadataFormValues, RadioPackageDetail>(
    { url: endpoint, method: 'PUT' },
    {
      mapValues: normalizePackageMetadata,
      onSuccess: (value) => {
        setItem(value);
        invalidateIndex();
        toast.success('Radio package has been updated.');
      },
    },
  );

  return (
    <Page>
      <Heading title="Radio package" />
      <Form noValidate autoComplete="off" onSubmit={onSubmit} initialValues={initialValues} disabled={!canChange}>
        <PackageMetadataFields />
        {canChange && (
          <div className="container px-0 ml-0">
            <Stack direction="horizontal" mb="3">
              <Submit>Update</Submit>
              <Button variant="secondary-danger" size="sm" icon="xmark" asChild>
                <Link to="delete">Delete</Link>
              </Button>
            </Stack>
          </div>
        )}
      </Form>
      <div className="container px-0 ml-0">
        <Heading title="Options">
          {canChange && (
            <Button variant="secondary" size="sm" icon="circle-plus" asChild>
              <Link to="options/create">Add option</Link>
            </Button>
          )}
        </Heading>
        <OptionSummaryTable options={item.options} />
      </div>
    </Page>
  );
}

function PackageDeleteScreen({ packagesUrl }: { packagesUrl: string }): ReactElement {
  const { endpoint } = useEntity<RadioPackageDetail>();
  const navigate = useNavigate();
  const invalidateIndex = useInvalidatePackageIndex(packagesUrl);
  const canChange = useHasPerm(packagePermissions.change);
  const onSubmit = useSubmitHandler(
    { url: endpoint, method: 'DELETE' },
    {
      onSuccess: () => {
        invalidateIndex();
        toast.success('Radio package has been deleted.');
        navigate('../../');
      },
      onFailure: actionErrorOrThrow,
    },
  );
  if (!canChange) return <PermissionDenied />;

  return (
    <Page>
      <Breadcrumb to={null}>Delete</Breadcrumb>
      <Heading title="Delete radio package" />
      <DeleteForm onSubmit={onSubmit} />
    </Page>
  );
}

function optionSummary(option: RadioPackageOption): RadioPackageOptionSummary {
  return {
    id: option.id,
    name: option.name,
    pricing_kind: option.pricing.kind,
    distribution_mode: option.distribution?.mode || null,
  };
}

function OptionCreateScreen({
  packagesUrl,
  radioUrl,
  vendorId,
}: {
  packagesUrl: string;
  radioUrl: string;
  vendorId: number;
}): ReactElement {
  const packageEntity = useEntity<RadioPackageDetail>();
  const optionsUrl = `${packageEntity.endpoint}options/`;
  const navigate = useNavigate();
  const invalidateIndex = useInvalidatePackageIndex(packagesUrl);
  const canChange = useHasPerm(packagePermissions.change);
  const onSubmit = useSubmitHandler<PackageOptionFormValues, RadioPackageOption>(
    { url: optionsUrl, method: 'POST' },
    {
      mapValues: normalizePackageOption,
      onSuccess: (value) => {
        packageEntity.setItem((item) => ({ ...item, options: [...item.options, optionSummary(value)] }));
        invalidateIndex();
        toast.success('Radio package option has been created.');
        navigate(`../${value.id}`);
      },
    },
  );

  if (!canChange) return <PermissionDenied />;

  return (
    <RadioFormData radioUrl={radioUrl} vendorId={vendorId}>
      {(radio, outlets) => (
        <Page>
          <Breadcrumb to={null}>Create option</Breadcrumb>
          <Heading title="Create radio package option" />
          <Form noValidate autoComplete="off" onSubmit={onSubmit} initialValues={createPackageOption()}>
            <PackageOptionFields bands={radio.bands} outlets={outlets} />
            <Submit>Create</Submit>
          </Form>
        </Page>
      )}
    </RadioFormData>
  );
}

function OptionForm({
  option,
  optionUrl,
  radio,
  outlets,
  updateOption,
}: {
  option: RadioPackageOption;
  optionUrl: string;
  radio: RadioPriceListRates;
  outlets: Outlet[];
  updateOption: (option: RadioPackageOption) => void;
}): ReactElement {
  const packageEntity = useEntity<RadioPackageDetail>();
  const canChange = useHasPerm(packagePermissions.change);
  const initialValues = useMemo(() => toPackageOptionFormValues(option), [option]);
  const onSubmit = useSubmitHandler<PackageOptionFormValues, RadioPackageOption>(
    { url: optionUrl, method: 'PUT' },
    {
      mapValues: normalizePackageOption,
      onSuccess: (value) => {
        updateOption(value);
        packageEntity.setItem((item) => ({
          ...item,
          options: item.options.map((summary) => (summary.id === value.id ? optionSummary(value) : summary)),
        }));
        toast.success('Radio package option has been updated.');
      },
    },
  );
  return (
    <Page>
      <Breadcrumb to={null}>{option.name}</Breadcrumb>
      <Heading title="Radio package option" />
      <Form noValidate autoComplete="off" onSubmit={onSubmit} initialValues={initialValues} disabled={!canChange}>
        <PackageOptionFields bands={radio.bands} outlets={outlets} />
        {canChange && (
          <div className="container px-0 ml-0">
            <Stack direction="horizontal" mb="3">
              <Submit>Update</Submit>
              <Button variant="secondary-danger" size="sm" icon="xmark" asChild>
                <Link to="delete">Delete</Link>
              </Button>
            </Stack>
          </div>
        )}
      </Form>
    </Page>
  );
}

function OptionDetailScreen({ radioUrl, vendorId }: { radioUrl: string; vendorId: number }): ReactElement {
  const { optionId } = useParams();
  const packageEntity = useEntity<RadioPackageDetail>();
  const optionUrl = `${packageEntity.endpoint}options/${optionId}/`;
  return (
    <ItemLoader<RadioPackageOption> url={optionUrl}>
      {({ item: option, setItem }) => (
        <RadioFormData radioUrl={radioUrl} vendorId={vendorId}>
          {(radio, outlets) => (
            <OptionForm option={option} optionUrl={optionUrl} radio={radio} outlets={outlets} updateOption={setItem} />
          )}
        </RadioFormData>
      )}
    </ItemLoader>
  );
}

function OptionDeleteScreen({ packagesUrl }: { packagesUrl: string }): ReactElement {
  const { optionId } = useParams();
  const packageEntity = useEntity<RadioPackageDetail>();
  const optionUrl = `${packageEntity.endpoint}options/${optionId}/`;
  const navigate = useNavigate();
  const invalidateIndex = useInvalidatePackageIndex(packagesUrl);
  const canChange = useHasPerm(packagePermissions.change);
  const onSubmit = useSubmitHandler(
    { url: optionUrl, method: 'DELETE' },
    {
      onSuccess: () => {
        packageEntity.setItem((item) => ({
          ...item,
          options: item.options.filter((option) => option.id !== Number(optionId)),
        }));
        invalidateIndex();
        toast.success('Radio package option has been deleted.');
        navigate('../../../');
      },
      onFailure: (error) => getDRFFormError(error) || actionErrorOrThrow(error),
    },
  );
  if (!canChange) return <PermissionDenied />;

  return (
    <Page>
      <Breadcrumb to={null}>Delete option</Breadcrumb>
      <Heading title="Delete radio package option" />
      <DeleteForm onSubmit={onSubmit}>
        <DeleteSubmissionError />
      </DeleteForm>
    </Page>
  );
}

function PackageRoutes({
  packagesUrl,
  radioUrl,
  vendorId,
}: {
  packagesUrl: string;
  radioUrl: string;
  vendorId: number;
}): ReactElement | null {
  return useRoutes([
    { index: true, element: <PackageDetailScreen packagesUrl={packagesUrl} /> },
    { path: 'delete', element: <PackageDeleteScreen packagesUrl={packagesUrl} /> },
    {
      path: 'options/create',
      element: <OptionCreateScreen packagesUrl={packagesUrl} radioUrl={radioUrl} vendorId={vendorId} />,
    },
    { path: 'options/:optionId', element: <OptionDetailScreen radioUrl={radioUrl} vendorId={vendorId} /> },
    { path: 'options/:optionId/delete', element: <OptionDeleteScreen packagesUrl={packagesUrl} /> },
  ]);
}

export function RadioPricelistPackages({ vendorId }: { vendorId: number }): ReactElement {
  const priceListEntity = useEntity<PriceList>();
  const priceListModel = useModel();
  invariant(priceListEntity.item.type === 'RADIO');

  const radioUrl = `${priceListEntity.endpoint}radio/`;
  const packagesUrl = `${radioUrl}packages/`;
  const routes = useRoutes([
    { index: true, element: <IndexScene columns={packageColumns} /> },
    {
      path: 'create',
      element: <PackageCreateScreen packagesUrl={packagesUrl} radioUrl={radioUrl} vendorId={vendorId} />,
    },
    {
      path: ':id/*',
      element: (
        <EntityScene<RadioPackageDetail>
          element={<PackageRoutes packagesUrl={packagesUrl} radioUrl={radioUrl} vendorId={vendorId} />}
        />
      ),
    },
  ]);

  return (
    <ModelScene<RadioPackageDetail>
      endpoint={packagesUrl}
      title="Radio package"
      pluralTitle="Radio packages"
      permissions={{ ...priceListModel.permissions, ...packagePermissions }}
      entityTitle={(radioPackage) => radioPackage.name}
    >
      {routes}
    </ModelScene>
  );
}
